import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { append, map, pathOr, prepend, reduce, remove, replace } from 'ramda'
import React from 'react'
import { v1 as uuid } from 'uuid'

import { Condition, PersonalizationType } from '@/app/types'

import ConditionGroup from './condition-group'
import { Groups, operands } from './definitions'
import { validateConditionValue } from './validation'

type Props = {
  data: Condition
  groups: Groups
  item?: any
  personalizationList: PersonalizationType
  segments: Groups
  title: string
  onClose: () => void
  onSaveConditions: (item: any) => void
}

export default function Conditions(props: Props) {
  const { groups, item, personalizationList, segments, title, onClose, onSaveConditions } = props

  const [data, setData] = React.useState(() => remove(0, 1, props.data))
  const [idArray, setIdArray] = React.useState<any[]>(() => map(() => uuid(), remove(0, 1, props.data)))
  const [submissionRequested, setSubmissionRequested] = React.useState(false)
  const invalid = React.useMemo(
    () =>
      reduce(
        (accum: boolean, conditionRow: any) =>
          accum ||
          reduce(
            (conditionAccum: boolean, condition: any) =>
              conditionAccum ||
              !pathOr('', [0], condition) ||
              !replace(/{|}/g, '', pathOr('', [1], condition)) ||
              !!validateConditionValue(condition),
            accum,
            remove(0, 1, conditionRow),
          ),
        false,
        data,
      ),
    [data],
  )

  return (
    <Dialog open={true} maxWidth="md" fullWidth={true}>
      <DialogTitle style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">Conditions</Typography>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div>
          {data.map((conditionRow, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                marginBottom: 18,
              }}
            >
              <div
                data-testid={`condition-group-${rowIndex}-title`}
                style={{
                  display: 'flex',
                  marginBottom: '10px',
                }}
              >
                {rowIndex === 0 ? (
                  <Typography variant="subtitle2">{`${title} only if the following conditions apply:`}</Typography>
                ) : (
                  <Typography variant="subtitle2">
                    <Typography color="primary.main" component="strong">
                      OR
                    </Typography>{' '}
                    if the following conditions apply:
                  </Typography>
                )}
              </div>
              <ConditionGroup
                conditionRow={conditionRow as Condition[]}
                error={submissionRequested}
                personalizationList={personalizationList}
                groups={groups}
                segments={segments}
                key={`row${idArray[rowIndex]}`}
                rowIndex={rowIndex}
                isFirst={rowIndex === 0}
                isLast={rowIndex === data.length - 1}
                groupedOperators={true}
                operands={operands}
                validateOnBlur
                onAddConditionGroup={() => {
                  setData(append(['and', ['', '', '']], data))
                  setIdArray(append(uuid(), idArray))
                }}
                onChangeCondition={({ updatedConditionGroup }: any) => {
                  const conditions = [...data]
                  if (updatedConditionGroup.length === 1) {
                    setData(remove(rowIndex, 1, conditions))
                    setIdArray(remove(rowIndex, 1, conditions))
                  } else {
                    conditions[rowIndex] = updatedConditionGroup
                    setData(conditions)
                  }
                }}
              />
              <Divider />
            </div>
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <div
          style={{
            width: '100%',
          }}
        >
          <Button
            style={{
              color: '#ff5252',
              marginLeft: '-10px',
            }}
            onClick={() => {
              onSaveConditions({
                ...item,
                when: null,
              })
            }}
            size="small"
          >
            Remove all conditions
          </Button>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSubmissionRequested(true)
            if (invalid) {
              return
            }
            const when = prepend('or', data)
            onSaveConditions({
              ...item,
              when,
            })
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
